import React, { useState, useRef, useEffect } from "react";
import "./DesignEditor.css";

const PencilIcon = ({ active }) => (
  <svg
    width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke={active ? "#09F984" : "#38bdf8"}
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
  >
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
  </svg>
);

// rgb(a) → #rrggbb
const rgbToHex = (rgb) => {
  const m = rgb.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!m) return null;
  return "#" + [m[1], m[2], m[3]]
    .map((n) => (+n).toString(16).padStart(2, "0"))
    .join("");
};

const isTransparent = (color) => {
  if (!color || color === "transparent") return true;
  // rgba(x,x,x,0)
  const m = color.match(/rgba\([^)]+,\s*([\d.]+)\)/);
  if (m && parseFloat(m[1]) === 0) return true;
  return false;
};

// DOM ağacını yukarı tara — ilk opak arka planı bul
const pickColorFromElement = (el) => {
  let cur = el;
  while (cur && cur !== document.documentElement) {
    const bg = getComputedStyle(cur).backgroundColor;
    if (!isTransparent(bg)) return rgbToHex(bg);
    cur = cur.parentElement;
  }
  return null;
};

const toHex6 = (value) => {
  if (!value) return '#000000';
  if (value.startsWith('#') && value.length >= 7) return value.slice(0, 7);
  return '#000000';
};

const ColorRow = ({ label, colorKey, value, isPickActive, onPencilClick, onColorChange }) => (
  <div className="color-input">
    <label>{label}</label>
    <button
      className={`color-edit-btn${isPickActive ? " active" : ""}`}
      onClick={() => onPencilClick(colorKey)}
      title={isPickActive ? "İptal (Esc)" : "Sayfadan renk seç"}
    >
      <PencilIcon active={isPickActive} />
    </button>
    <label className="color-swatch-container" style={{ cursor: 'pointer' }}>
      <div className="color-swatch" style={{ backgroundColor: value }} />
      <input
        type="color"
        value={toHex6(value)}
        onChange={(e) => onColorChange(colorKey, e.target.value)}
        style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
      />
    </label>
    <span className="color-value-text">{value}</span>
  </div>
);

const DesignEditor = ({ colors, onColorChange, onClose, customBgImage, onBgImageUpload, onBgImageRemove }) => {
  const [pickMode, setPickMode] = useState({ active: false, key: null });
  const [bgUploading, setBgUploading] = useState(false);
  const bgFileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({
    x: window.innerWidth - 420,
    y: Math.max(20, (window.innerHeight - 700) / 2),
  });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const editorRef = useRef(null);
  const onColorChangeRef = useRef(onColorChange);
  useEffect(() => { onColorChangeRef.current = onColorChange; }, [onColorChange]);

  const handlePencilClick = async (key) => {
    // Aynı kaleme tekrar basılırsa iptal
    if (pickMode.active && pickMode.key === key) {
      setPickMode({ active: false, key: null });
      return;
    }

    // --- EyeDropper API (Chrome / Edge 95+) ---
    if (typeof window.EyeDropper !== "undefined") {
      try {
        const dropper = new window.EyeDropper();
        const { sRGBHex } = await dropper.open();
        onColorChangeRef.current(key, sRGBHex);
      } catch {
        // kullanıcı iptal etti
      }
      return;
    }

    // --- Fallback: DOM pick modu ---
    setPickMode({ active: true, key });
  };

  // DOM pick modu (EyeDropper yoksa)
  useEffect(() => {
    if (!pickMode.active) return;

    document.body.classList.add("color-pick-mode");

    const pick = (e) => {
      // Editörün içine tıklanırsa yoksay
      if (editorRef.current?.contains(e.target)) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      const hex = pickColorFromElement(e.target);
      if (hex) onColorChangeRef.current(pickMode.key, hex);

      setPickMode({ active: false, key: null });
    };

    const cancel = (e) => {
      if (e.key === "Escape") setPickMode({ active: false, key: null });
    };

    document.addEventListener("click", pick, true);
    document.addEventListener("keydown", cancel);

    return () => {
      document.body.classList.remove("color-pick-mode");
      document.removeEventListener("click", pick, true);
      document.removeEventListener("keydown", cancel);
    };
  }, [pickMode.active, pickMode.key]);

  const handleBgFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setBgUploading(true);
    try {
      await onBgImageUpload(file);
    } finally {
      setBgUploading(false);
    }
  };

  const handleMouseDown = (e) => {
    if (e.target.closest(".editor-header")) {
      setIsDragging(true);
      const rect = editorRef.current.getBoundingClientRect();
      setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;
      const maxX = window.innerWidth - editorRef.current.offsetWidth;
      const maxY = window.innerHeight - editorRef.current.offsetHeight;
      setPosition({
        x: Math.min(Math.max(0, newX), maxX),
        y: Math.min(Math.max(0, newY), maxY),
      });
    }
  };

  const handleMouseUp = () => setIsDragging(false);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const row = (label, key, fallback) => (
    <ColorRow
      key={key}
      label={label}
      colorKey={key}
      value={colors[key] || fallback || "#000000"}
      isPickActive={pickMode.active && pickMode.key === key}
      onPencilClick={handlePencilClick}
      onColorChange={onColorChange}
    />
  );

  return (
    <div
      ref={editorRef}
      className={`css-editor design-editor-panel ${isDragging ? "dragging" : ""}`}
      style={{ position: "fixed", left: position.x, top: position.y, cursor: isDragging ? "grabbing" : "auto" }}
      onMouseDown={handleMouseDown}
    >
      <div className="editor-header" style={{ cursor: "grab" }}>
        <h3>Tasarım Editörü</h3>
        <button className="close-editor" onClick={onClose}>×</button>
      </div>

      {pickMode.active && (
        <div className="pick-hint">
          <span>Sayfada bir renge tıkla</span>
          <kbd>Esc</kbd>
          <span>iptal</span>
        </div>
      )}

      <div className="editor-section bg-image-section">
        <h4>Arkaplan Görseli</h4>

        <div className="bg-preview-box">
          {customBgImage ? (
            <img src={customBgImage} alt="Arkaplan" className="bg-preview-img" />
          ) : (
            <div className="bg-preview-empty">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="3"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <path d="M21 15l-5-5L5 21"/>
              </svg>
              <span>Henüz görsel yüklenmedi</span>
            </div>
          )}
        </div>

        <div className="bg-controls-row">
          <input
            ref={bgFileInputRef}
            type="file"
            accept="image/*"
            onChange={handleBgFileChange}
            style={{ display: 'none' }}
          />
          <button
            className={`bg-upload-btn${bgUploading ? ' loading' : ''}`}
            onClick={() => bgFileInputRef.current?.click()}
            disabled={bgUploading}
          >
            {bgUploading ? (
              <><span className="bg-spinner" /> Yükleniyor...</>
            ) : (
              <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> Görsel Seç</>
            )}
          </button>
          {customBgImage && (
            <button className="bg-remove-btn" onClick={onBgImageRemove} disabled={bgUploading}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/></svg>
              Kaldır
            </button>
          )}
        </div>

        <label className="bg-toggle-row" style={{ pointerEvents: bgUploading ? 'none' : 'auto' }}>
          <div className={`bg-toggle-switch${colors.customBgEnabled ? ' on' : ''}`}
            onClick={() => onColorChange('customBgEnabled', !colors.customBgEnabled)}
          >
            <div className="bg-toggle-knob" />
          </div>
          <span className="bg-toggle-label">
            {colors.customBgEnabled ? 'Özel Arkaplan Aktif' : 'Özel Arkaplan Devre Dışı'}
          </span>
        </label>
      </div>

      <div className="editor-section">
        <h4>Genel Tasarım</h4>
        {row("Arkaplan Gradient 1:", "backgroundGradient1")}
        {row("Arkaplan Gradient 2:", "backgroundGradient2")}
      </div>

      <div className="editor-section">
        <h4>Header Tasarımı</h4>
        {row("Header Arkaplan:", "headerBg")}
        {row("Header Item Arkaplan:", "headerItemBg")}
        {row("Header Kenar:", "headerBorderColor")}
        {row("İkon Rengi:", "headerIconColor")}
      </div>

      <div className="editor-section">
        <h4>Buton Tasarımı</h4>
        {row("Gradient 1:", "buttonGradient1")}
        {row("Gradient 2:", "buttonGradient2")}
        {row("Yazı Rengi:", "buttonTextColor")}
        {row("Kenar Rengi:", "buttonBorderColor")}
        {row("Parıltı Rengi:", "buttonGlowColor")}
      </div>

      <div className="editor-section">
        <h4>Tebrikler Modal Tasarımı</h4>
        {row("Modal Arkaplan:", "modalBg", "#1A2234")}
        {row("Modal Kenar:", "modalBorderColor", "#09F984")}
        {row("Modal Başlık Rengi:", "modalHeaderColor", "#FFFFFF")}
        {row("Modal Metin Rengi:", "modalTextColor", "#E0E6ED")}
        {row("Modal Buton Gradient 1:", "modalButtonGradient1", "#1A2234")}
        {row("Modal Buton Gradient 2:", "modalButtonGradient2", "#2E4057")}
      </div>

      <div className="editor-section">
        <h4>Geçmiş Modal Tasarımı</h4>
        {row("Modal Arkaplan:", "historyModalBg", "#1A2234")}
        {row("Modal Kenar:", "historyModalBorder", "#09F984")}
        {row("Header Arkaplan:", "historyHeaderBg", "#FFFFFF")}
        {row("Başlık Rengi:", "historyHeaderColor", "#FFFFFF")}
        {row("Kapat Buton Rengi:", "historyCloseColor", "#FFFFFF")}
        {row("Tarih Metin Rengi:", "historyDateColor", "#94A3B8")}
        {row("Durum Badge Arkaplan:", "historyBadgeBg", "#09F984")}
        {row("Durum Badge Metin:", "historyBadgeText", "#1A2234")}
        {row("Bilgi Başlık Rengi:", "historyInfoLabel", "#94A3B8")}
        {row("Bilgi Değer Rengi:", "historyInfoValue", "#E2E8F0")}
        {row("Not Metin Rengi:", "historyNoteColor", "#94A3B8")}
        {row("Item Arkaplan:", "historyItemBg", "#2E4057")}
        {row("Item Kenar:", "historyItemBorder", "#09F984")}
      </div>

      <div className="editor-section">
        <h4>Spin Hakkı Yok Modal Tasarımı</h4>
        {row("Overlay Arkaplan:", "noSpinOverlayBg", "rgba(15, 23, 42, 0.95)")}
        {row("Modal Gradient 1:", "noSpinModalGradient1", "#1A2234")}
        {row("Modal Gradient 2:", "noSpinModalGradient2", "#2E4057")}
        {row("Modal Kenar:", "noSpinModalBorder", "rgba(255, 255, 255, 0.1)")}
        {row("Başlık Rengi:", "noSpinModalTitle", "#FFFFFF")}
        {row("Metin Rengi:", "noSpinModalText", "rgba(255, 255, 255, 0.8)")}
      </div>
    </div>
  );
};

export default DesignEditor;
